import { themes } from '@lifebit-ai/lds'
import { Providers, ResultsState } from 'enums'

import isObject from 'lodash/isObject'
import startCase from 'lodash/startCase'
import { NO_CONTENTS_MESSAGE } from 'modules/resultsV2/components/FileContent/utils'
import { Result, ResultRequestType } from 'services/results/types'
import { getAttestationFormTypeLabel } from 'utils/additionalInfoFormCopy'

import type { DownloadExpiryOption } from './downloadExpiry.types'
import { DownloadExpiryKeys } from './enums'

export const APPROVAL_ALERT_TEST_ID = 'approval-alert'
export const APPROVE_BUTTON_TEST_ID = 'approve-button'
export const REQUEST_ATTESTATION_BUTTON_TEST_ID = 'request-attestation-button'
export const REQUEST_ADDITIONAL_INFORMATION_BUTTON_TEST_ID = 'request-additional-information-button'
export const REQUEST_ADDITIONAL_INFORMATION_MODAL_TEST_ID = 'request-additional-information-modal'
export const REQUEST_ADDITIONAL_INFORMATION_REASON_FIELD_TEST_ID = 'request-additional-information-reason'
export const REQUEST_ADDITIONAL_INFORMATION_SUBMIT_TEST_ID = 'submit-request-additional-information-button'
export const REJECT_BUTTON_TEST_ID = 'reject-button'
export const DOWNLOAD_FILE_ICON_TEST_ID = 'download-file-icon'
export const COPY_S3_LINK_ICON_TEST_ID = 'copy-s3-link-icon'
export const VIEW_DETAILS_ICON_TEST_ID = 'view-details-icon'
export const AUTOMATIC_APPROVAL_ALERT_TEST_ID = 'automatic-approval-alert'
export const RE_REQUEST_WITH_UPDATED_FILE_ALERT_TEST_ID = 're-request-with-updated-file-alert'
export const RE_REQUEST_BY_DIFFERENT_USER_ALERT_TEST_ID = 're-request-by-different-user-alert'

const COMPRESSED_FILE_EXTENSIONS = new Set([
  '7z',
  'bz2',
  'gz',
  'gzip',
  'lz4',
  'lzma',
  'rar',
  'tar',
  'tbz',
  'tbz2',
  'tgz',
  'txz',
  'xz',
  'zip',
  'zst'
])

export const isCompressedFileName = (fileName: string): boolean => {
  const extension = fileName.split('.').pop()?.toLowerCase()
  if (!extension || extension === fileName.toLowerCase()) return false

  return COMPRESSED_FILE_EXTENSIONS.has(extension)
}

export const getFileContent = (data: string | Record<string, string> | undefined): string => {
  if (!data) return NO_CONTENTS_MESSAGE
  if (isObject(data)) return JSON.stringify(data)

  // Stringifying the data makes sure edge cases returned by the backend are dealt with as well
  return data.toString()
}

const ONE_MINUTE_IN_SECONDS = 60

export const downloadExpiryOptions: Record<DownloadExpiryKeys, DownloadExpiryOption> = {
  [DownloadExpiryKeys.OneMinute]: {
    label: '1 Minute',
    timeInSeconds: ONE_MINUTE_IN_SECONDS
  },
  [DownloadExpiryKeys.FiveMinutes]: {
    label: '5 Minutes',
    timeInSeconds: 5 * ONE_MINUTE_IN_SECONDS
  },
  [DownloadExpiryKeys.OneHour]: {
    label: '1 Hour',
    timeInSeconds: 60 * ONE_MINUTE_IN_SECONDS
  },
  [DownloadExpiryKeys.FiveHours]: {
    label: '5 Hours',
    timeInSeconds: 5 * 60 * ONE_MINUTE_IN_SECONDS
  },
  [DownloadExpiryKeys.OneDay]: {
    label: '1 Day',
    timeInSeconds: 24 * 60 * ONE_MINUTE_IN_SECONDS
  },
  [DownloadExpiryKeys.TwoDays]: {
    label: '2 Days',
    timeInSeconds: 2 * 24 * 60 * ONE_MINUTE_IN_SECONDS
  },
  [DownloadExpiryKeys.FiveDays]: {
    label: '5 Days',
    timeInSeconds: 5 * 24 * 60 * ONE_MINUTE_IN_SECONDS
  },
  [DownloadExpiryKeys.SevenDays]: {
    label: '7 Days',
    timeInSeconds: 7 * 24 * 60 * ONE_MINUTE_IN_SECONDS
  }
}

export const isResultDownloadLinkExpired = (result: Result, downloadLinkExpiresInSeconds: number): boolean => {
  return (
    result.status === ResultsState.Complete &&
    !!result.verifiedAt &&
    typeof downloadLinkExpiresInSeconds === 'number' &&
    Date.now() > new Date(result.verifiedAt).getTime() + downloadLinkExpiresInSeconds * 1000
  )
}

export const getWorkspaceDownloadExpiryOptionKey = (
  downloadLinkExpiresInSeconds: number
): DownloadExpiryKeys | null => {
  const downloadExpiryOptionKey = Object.keys(downloadExpiryOptions).find(
    key => downloadExpiryOptions[key as unknown as DownloadExpiryKeys].timeInSeconds === downloadLinkExpiresInSeconds
  )

  return downloadExpiryOptionKey === undefined ? null : (Number(downloadExpiryOptionKey) as DownloadExpiryKeys)
}

export const isResultPendingVerification = (result: Result): boolean => {
  return result.status === ResultsState.PendingVerification
}

export const isReRequest = (result: Result): boolean => {
  return !!result.fileChangeStatus && !!result.reRequest
}

export const isAppealRequest = (result: Result): boolean => {
  return result.requestType === ResultRequestType.AppealRequest
}

export const isDifferentRequester = (result: Result): boolean => {
  return (
    !!result.reRequest?.originallyApprovedBy && result.reRequest.originallyApprovedBy !== result.requestingUser.email
  )
}

export const getFormTypeLabel = (formType: string): string => {
  if (formType === 'attestation') {
    return getAttestationFormTypeLabel()
  }

  return `${startCase(formType.split('-').join(' '))} Form`
}

export interface CloudStorageUriLocation {
  provider?: Providers | string
  bucket?: string
  storageAccountName?: string
  containerName?: string
}

const normalizeCloudStorageKey = (fileKey: string): string => fileKey.replace(/^\/+/, '')

/** Builds a cloud storage URI for clipboard / CLI use (`s3://…` or `az://…`). */
export const formatCloudStorageUri = (location: CloudStorageUriLocation, fileKey: string): string => {
  const normalizedKey = normalizeCloudStorageKey(fileKey)

  if (location.provider === Providers.Azure) {
    const azureParts = [location.storageAccountName, location.containerName, normalizedKey].filter(
      (part): part is string => !!part
    )

    return `az://${azureParts.join('/')}`
  }

  const bucket = location.bucket ?? ''

  return `s3://${bucket}/${normalizedKey}`
}

export const getCopyCloudStorageLinkTooltip = (location: CloudStorageUriLocation): string =>
  location.provider === Providers.Azure ? 'Copy Azure link' : 'Copy S3 link'

/** @deprecated Use {@link formatCloudStorageUri} with a location object instead. */
export const formatS3Uri = (bucket: string, fileKey: string): string =>
  formatCloudStorageUri({ provider: Providers.AWS, bucket }, fileKey)

export const DATA_ACCESS_REQUEST_FORM_TITLE = 'Data access request'

/** Locale-formatted whole numbers for cohort sizes (e.g. `1,250`). */
export const formatCompactParticipantCount = (participantCount: number): string =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(participantCount)

export const DATA_ACCESS_REQUEST_PERSON_ID_FILE_TITLE = 'Person ID file'

export const PERSON_ID_FILE_PREPARING_MESSAGE = 'Preparing the file. It will be available to download shortly.'

export const PERSON_ID_FILE_FAILED_MESSAGE =
  'The file could not be prepared. If this issue persists, please contact your administrator.'

export const PARTICIPANT_LIST_REQUIRED_TOOLTIP = 'This request requires a participant list.'

export const DATA_ACCESS_REQUEST_DETAILS_UNAVAILABLE_MESSAGE = 'Data access request details are unavailable.'

export const DEFAULT_DATA_ACCESS_REQUEST_DATASET_COLOR = themes.Light.color.chart['02']

export const EXPORT_REQUEST_APPROVAL_SUCCESS_MESSAGE = 'Export approval request sent'

export const DATA_ACCESS_REQUEST_APPROVAL_SUCCESS_MESSAGE = 'Data access request approved successfully'
